// PDP Coupons Strip — Web Component handler.
// Click "Áp dụng" → POST /cart/update.js → cart:refresh + open cart-drawer.
// Sync state khi page load + khi cart:change từ theme.
//
// API ref: Shopify Cart AJAX accepts `discount` field từ 2023+.
//   POST /cart/update.js  body: { discount: "CODE" }
//   Response: full cart object có discount_codes[]

(function () {
  function formatVND(amount) {
    // VND zero-decimal: vd 60000 → "60.000 ₫"
    return new Intl.NumberFormat("vi-VN").format(amount) + " ₫";
  }

  class PdpCouponsStrip extends HTMLElement {
    connectedCallback() {
      this._busy = false;

      this.querySelectorAll("[data-code]").forEach((item) => {
        const btn = item.querySelector(".pdp-coupons-strip__btn");
        btn?.addEventListener("click", () => this.handleApply(item));
      });

      this.syncFromCart();
      document.addEventListener("cart:change", () => this.syncFromCart());
    }

    async syncFromCart() {
      try {
        const r = await fetch(`${Shopify.routes.root}cart.js`, {
          credentials: "same-origin",
        });
        if (!r.ok) return;
        const cart = await r.json();
        const appliedCodes = (cart.discount_codes || [])
          .filter((d) => d.applicable)
          .map((d) => d.code.toUpperCase());

        // VND zero-decimal: price = giá thực tế. Hostproduct passed via attr (price 1 unit).
        const productPrice = parseInt(this.dataset.productPrice || "0", 10);
        const cartSubtotal = cart.items_subtotal_price ?? 0;

        this.querySelectorAll("[data-code]").forEach((item) => {
          const code = item.getAttribute("data-code").toUpperCase();
          if (appliedCodes.includes(code)) {
            this.setItemState(item, "applied");
            return;
          }
          // Check eligibility: cart + product này thêm 1 unit có đạt min không?
          const min = parseInt(item.dataset.minSubtotal || "0", 10);
          if (min > 0) {
            const potential = cartSubtotal + productPrice;
            if (potential < min) {
              const remaining = min - potential;
              this.setItemState(item, "ineligible", formatVND(remaining));
              return;
            }
          }
          this.setItemState(item, "idle");
        });
      } catch (e) {
        // Silent — cart fetch fail không phải fatal cho strip
      }
    }

    setItemState(item, state, message) {
      const btn = item.querySelector(".pdp-coupons-strip__btn");
      const label = item.querySelector(".pdp-coupons-strip__btn-label");
      const error = item.querySelector(".pdp-coupons-strip__error");

      item.dataset.state = state;
      btn.disabled = state === "loading" || state === "applied" || state === "ineligible";

      const labels = {
        idle: "Áp dụng",
        loading: "Đang áp dụng...",
        applied: "Đã áp dụng",
        error: "Thử lại",
        ineligible: message ? `Cần thêm ${message}` : "Chưa đủ điều kiện",
      };
      label.textContent = labels[state] || "Áp dụng";

      if (error) {
        error.hidden = state !== "error";
        error.textContent = state === "error" ? message || "Mã không hợp lệ" : "";
      }
    }

    findCurrentVariantId() {
      // Tìm variant ID từ product form trên PDP. Theme Impact V6 dùng
      // <form is="product-form"> với <input name="id" value="...">.
      // Có thể có sticky bar form duplicate — pick form đầu tiên có value valid.
      const inputs = document.querySelectorAll(
        'form[id^="product-form-"] input[name="id"]:not([disabled]), [is="product-form"] input[name="id"]:not([disabled])',
      );
      for (const input of inputs) {
        if (input.value && input.value !== "undefined") {
          return input.value;
        }
      }
      return null;
    }

    async addCurrentProductToCart() {
      const variantId = this.findCurrentVariantId();
      if (!variantId) {
        return { ok: false, error: "Không tìm thấy sản phẩm để thêm" };
      }
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );
      const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionsToBundle,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        return { ok: false, error: errBody.description || "Không thêm được sản phẩm" };
      }
      return { ok: true };
    }

    async handleApply(item) {
      if (this._busy) return;
      const code = item.getAttribute("data-code");
      this._busy = true;
      this.setItemState(item, "loading");

      try {
        // Step 1: nếu cart trống → tự thêm product hiện tại trước
        const currentCart = await fetch(`${Shopify.routes.root}cart.js`, {
          credentials: "same-origin",
        }).then((r) => r.json());

        if (currentCart.item_count === 0) {
          const addResult = await this.addCurrentProductToCart();
          if (!addResult.ok) {
            this.setItemState(item, "error", addResult.error);
            return;
          }
          console.log("[pdp-coupons] auto-added product to empty cart");
        }

        // Step 2: apply discount
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          }),
        );

        const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            discount: code,
            sections: sectionsToBundle,
          }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          this.setItemState(
            item,
            "error",
            errBody.description || "Mã không áp dụng được",
          );
          return;
        }

        const cart = await res.json();
        const matched = (cart.discount_codes || []).find(
          (d) => d.code.toUpperCase() === code.toUpperCase(),
        );

        if (!matched) {
          this.setItemState(item, "error", "Mã không tồn tại trong hệ thống");
          return;
        }

        if (!matched.applicable) {
          this.setItemState(
            item,
            "error",
            "Đơn hàng chưa đủ điều kiện áp mã này",
          );
          return;
        }

        this.setItemState(item, "applied");

        // Dispatch cart:change với cart object — theme listener sẽ apply
        // rendered sections vào DOM (drawer, icon bubble, etc.).
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "pdp-coupons:apply",
              cart: cart,
            },
          }),
        );

        // Mở drawer SAU khi cart:change để DOM đã update.
        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.show === "function") {
          drawer.show();
        }

        console.log(
          "[pdp-coupons] applied %s | total=%d discount=%d",
          code,
          cart.total_price,
          cart.total_discount,
        );
      } catch (err) {
        this.setItemState(item, "error", "Lỗi kết nối, vui lòng thử lại");
        console.warn("[pdp-coupons]", err);
      } finally {
        this._busy = false;
      }
    }
  }

  if (!customElements.get("pdp-coupons-strip")) {
    customElements.define("pdp-coupons-strip", PdpCouponsStrip);
  }
})();
